import { useEffect, useState } from "react";
import client, { apiError } from "../../api/client";
import { toast } from "sonner";
import { Plus, Trash2, Check, X } from "lucide-react";

const inputCls = "h-9 px-3 rounded-md border border-input bg-background text-sm outline-none focus:ring-2 focus:ring-ring/20";

export default function CatalogManager({ title, endpoint, hasNote = true, hasPrice = false }) {
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const [note, setNote] = useState("");
  const [price, setPrice] = useState("");
  const [editing, setEditing] = useState(null);

  const load = () => client.get(endpoint).then((res) => setItems(res.data));
  useEffect(() => { load(); }, [endpoint]);

  const add = async () => {
    if (!name.trim()) return;
    try {
      const body = { name: name.trim(), note: note.trim() };
      if (hasPrice) body.default_price = price !== "" ? Number(price) : null;
      await client.post(endpoint, body);
      setName(""); setNote(""); setPrice(""); load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const save = async (item) => {
    try {
      const body = { name: item.name, note: item.note };
      if (hasPrice) body.default_price = item.default_price === "" || item.default_price == null ? null : Number(item.default_price);
      await client.put(`${endpoint}/${item.id}`, body);
      setEditing(null); load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const del = async (id) => {
    if (!window.confirm("Opravdu smazat?")) return;
    try { await client.delete(`${endpoint}/${id}`); load(); } catch (err) { toast.error(apiError(err)); }
  };

  const upd = (id, k, v) => setItems((s) => s.map((x) => x.id === id ? { ...x, [k]: v } : x));

  return (
    <div className="max-w-2xl">
      <h2 className="font-heading text-lg font-medium mb-4">{title}</h2>
      <div className="flex gap-2 mb-4">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Název" className={inputCls + " flex-1"} data-testid={`catalog-name-${endpoint}`} />
        {hasPrice && <input type="number" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="Výchozí cena (Kč)" className={inputCls + " w-40"} data-testid="catalog-price" />}
        {hasNote && <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Poznámka" className={inputCls + " flex-1"} />}
        <button onClick={add} data-testid={`catalog-add-${endpoint}`} className="h-9 px-4 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 flex items-center gap-1"><Plus className="h-4 w-4" /> Přidat</button>
      </div>
      <div className="border border-border rounded-lg divide-y divide-border">
        {items.map((it) => (
          <div key={it.id} className="flex items-center gap-2 px-4 py-2.5" data-testid={`catalog-item-${it.id}`}>
            {editing === it.id ? (
              <>
                <input value={it.name} onChange={(e) => upd(it.id, "name", e.target.value)} className={inputCls + " flex-1"} />
                {hasPrice && <input type="number" value={it.default_price ?? ""} onChange={(e) => upd(it.id, "default_price", e.target.value)} className={inputCls + " w-32"} placeholder="Cena" />}
                {hasNote && <input value={it.note || ""} onChange={(e) => upd(it.id, "note", e.target.value)} className={inputCls + " flex-1"} />}
                <button onClick={() => save(it)} className="p-1.5 text-emerald-600 hover:bg-secondary rounded"><Check className="h-4 w-4" /></button>
                <button onClick={() => { setEditing(null); load(); }} className="p-1.5 text-muted-foreground hover:bg-secondary rounded"><X className="h-4 w-4" /></button>
              </>
            ) : (
              <>
                <div className="flex-1">
                  <span className="text-sm font-medium">{it.name}</span>
                  {hasPrice && it.default_price != null && <span className="text-sm text-emerald-700 ml-2">{it.default_price} Kč</span>}
                  {it.note && <span className="text-sm text-muted-foreground ml-2">— {it.note}</span>}
                </div>
                <button onClick={() => setEditing(it.id)} className="text-xs text-muted-foreground hover:text-foreground px-2">Upravit</button>
                <button onClick={() => del(it.id)} className="p-1.5 text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded"><Trash2 className="h-4 w-4" /></button>
              </>
            )}
          </div>
        ))}
        {items.length === 0 && <div className="px-4 py-8 text-center text-sm text-muted-foreground">Žádné položky</div>}
      </div>
    </div>
  );
}
